/**
 * Lector de archivos para la carga masiva: Excel (.xlsx, .xls) y CSV.
 *
 * Devuelve siempre texto: los encabezados de la primera fila con datos y una
 * fila por registro ({ encabezado: valor }), sin filas vacías y con los
 * valores recortados. Así la carga masiva no depende del formato de origen.
 */

export const BULK_FILE_ACCEPT = ".xlsx,.xls,.csv"

export interface ParsedBulkRow {
  /** Número de fila en el archivo (1 = encabezados) */
  rowNumber: number
  values: Record<string, string>
}

export interface ParsedBulkFile {
  headers: string[]
  rows: ParsedBulkRow[]
  /** Solo Excel: hoja que se leyó y cuántas hojas tiene el libro */
  sheetName?: string
  sheetCount?: number
}

type FileKind = "excel" | "csv"

export function getBulkFileKind(fileName: string): FileKind | null {
  const name = fileName.toLowerCase()
  if (name.endsWith(".xlsx") || name.endsWith(".xls")) return "excel"
  if (name.endsWith(".csv")) return "csv"
  return null
}

export async function parseBulkFile(file: File): Promise<ParsedBulkFile> {
  const kind = getBulkFileKind(file.name)
  if (!kind) throw new Error(`Formato no soportado: ${file.name}`)

  const buffer = await file.arrayBuffer()
  if (kind === "csv") {
    const text = decodeCsvBytes(buffer)
    return toParsedFile(parseCsv(text))
  }

  const sheet = await readExcelSheet(buffer)
  return { ...toParsedFile(sheet.cells), sheetName: sheet.name, sheetCount: sheet.count }
}

// ---------------------------------------------------------------------------
// CSV
// ---------------------------------------------------------------------------

/**
 * UTF-8 si el archivo es UTF-8 válido (el BOM de "CSV UTF-8" de Excel se
 * descarta solo); si no, Windows-1252, que es lo que guarda Excel en
 * "CSV (delimitado por comas)". Así los acentos y la ñ salen bien en ambos.
 */
export function decodeCsvBytes(buffer: ArrayBuffer): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer)
  } catch {
    return new TextDecoder("windows-1252").decode(buffer)
  }
}

/** Separador más frecuente en la primera fila (fuera de comillas): coma, punto y coma o tabulador */
export function detectDelimiter(text: string): string {
  const counts: Record<string, number> = { ",": 0, ";": 0, "\t": 0 }
  let inQuotes = false
  for (const ch of text) {
    if (ch === '"') inQuotes = !inQuotes
    else if (!inQuotes && (ch === "\n" || ch === "\r")) break
    else if (!inQuotes && ch in counts) counts[ch]++
  }
  let best = ","
  for (const delimiter of [";", "\t"]) {
    if (counts[delimiter] > counts[best]) best = delimiter
  }
  return best
}

/**
 * CSV según RFC 4180: los campos entre comillas pueden tener separadores,
 * saltos de línea y comillas dobles escapadas (""). Acepta \r\n, \n y \r.
 */
export function parseCsv(text: string, delimiter = detectDelimiter(text)): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let field = ""
  let inQuotes = false
  let fieldStarted = false
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text

  const endField = () => {
    row.push(field)
    field = ""
    fieldStarted = false
  }
  const endRow = () => {
    endField()
    rows.push(row)
    row = []
  }

  for (let i = 0; i < src.length; i++) {
    const ch = src[i]
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"'
          i++
        } else {
          inQuotes = false
        }
      } else {
        field += ch
      }
    } else if (ch === '"' && !fieldStarted) {
      inQuotes = true
      fieldStarted = true
    } else if (ch === delimiter) {
      endField()
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++
      endRow()
    } else {
      field += ch
      fieldStarted = true
    }
  }
  if (field !== "" || fieldStarted || row.length > 0) endRow()
  return rows
}

// ---------------------------------------------------------------------------
// Excel
// ---------------------------------------------------------------------------

interface SsfDate {
  y: number
  m: number
  d: number
  H: number
  M: number
  S: number
}

interface SsfLike {
  is_date(format: string): boolean
  parse_date_code(value: number, opts?: { date1904?: boolean }): SsfDate | null
}

interface ExcelCell {
  t?: string
  v?: unknown
  z?: string | number
}

/** Primera hoja visible con datos, como matriz de texto */
async function readExcelSheet(buffer: ArrayBuffer): Promise<{ cells: string[][]; name: string; count: number }> {
  const XLSX = await import("xlsx")
  const workbook = XLSX.read(new Uint8Array(buffer), { type: "array", cellNF: true, cellDates: false })
  const ssf = XLSX.SSF as unknown as SsfLike
  const date1904 = Boolean(workbook.Workbook?.WBProps?.date1904)

  const hidden = (index: number) => Boolean(workbook.Workbook?.Sheets?.[index]?.Hidden)
  const candidates = workbook.SheetNames.filter((_, index) => !hidden(index))
  const name =
    candidates.find((sheetName) => workbook.Sheets[sheetName]?.["!ref"]) ?? candidates[0] ?? workbook.SheetNames[0]
  const sheet = workbook.Sheets[name]
  const count = workbook.SheetNames.length
  if (!sheet?.["!ref"]) return { cells: [], name, count }

  const range = XLSX.utils.decode_range(sheet["!ref"])
  const cells: string[][] = []
  for (let r = range.s.r; r <= range.e.r; r++) {
    const row: string[] = []
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cell = sheet[XLSX.utils.encode_cell({ r, c })] as ExcelCell | undefined
      row.push(excelCellToText(cell, ssf, date1904))
    }
    cells.push(row)
  }
  return { cells, name, count }
}

function excelCellToText(cell: ExcelCell | undefined, ssf: SsfLike, date1904: boolean): string {
  if (!cell || cell.v === undefined || cell.v === null) return ""
  switch (cell.t) {
    case "n": {
      const value = cell.v as number
      if (typeof cell.z === "string" && ssf.is_date(cell.z)) {
        return formatExcelDate(ssf.parse_date_code(value, { date1904 }), value)
      }
      return String(value)
    }
    case "b":
      return cell.v ? "true" : "false"
    case "e":
      // Celdas con error (#N/A, #REF!...)
      return ""
    case "d": {
      const date = cell.v as Date
      return formatParts(date.getFullYear(), date.getMonth() + 1, date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds())
    }
    default:
      return String(cell.v)
  }
}

/** Fecha de Excel como "AAAA-MM-DD" o "AAAA-MM-DD HH:MM:SS", sin pasar por la zona horaria */
function formatExcelDate(parts: SsfDate | null, raw: number): string {
  if (!parts) return String(raw)
  return formatParts(parts.y, parts.m, parts.d, parts.H, parts.M, parts.S)
}

function formatParts(y: number, m: number, d: number, H: number, M: number, S: number): string {
  const pad = (n: number) => String(n).padStart(2, "0")
  const date = `${y}-${pad(m)}-${pad(d)}`
  return H || M || S ? `${date} ${pad(H)}:${pad(M)}:${pad(S)}` : date
}

// ---------------------------------------------------------------------------
// Común
// ---------------------------------------------------------------------------

/** Matriz de texto → encabezados + filas; la primera fila no vacía son los encabezados */
function toParsedFile(cells: string[][]): ParsedBulkFile {
  const isEmpty = (row: string[]) => row.every((value) => value.trim() === "")
  const headerIndex = cells.findIndex((row) => !isEmpty(row))
  if (headerIndex === -1) return { headers: [], rows: [] }

  const headers = uniqueHeaders(cells[headerIndex])
  const rows: ParsedBulkRow[] = []
  for (let i = headerIndex + 1; i < cells.length; i++) {
    const row = cells[i]
    if (isEmpty(row)) continue
    // fromEntries crea propiedades propias: un encabezado "__proto__" no toca el prototipo
    const values = Object.fromEntries(headers.map((header, col) => [header, (row[col] ?? "").trim()]))
    rows.push({ rowNumber: i + 1, values })
  }
  return { headers, rows }
}

/** Encabezados recortados; los vacíos se nombran "Columna N" y los repetidos llevan " (2)", " (3)"... */
function uniqueHeaders(raw: string[]): string[] {
  let last = raw.length
  while (last > 0 && raw[last - 1].trim() === "") last--

  const seen = new Map<string, number>()
  return raw.slice(0, last).map((value, index) => {
    const base = value.trim() || `Columna ${index + 1}`
    const times = (seen.get(base) ?? 0) + 1
    seen.set(base, times)
    return times === 1 ? base : `${base} (${times})`
  })
}
